import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { subDays, format, startOfDay } from 'date-fns';

export interface SyncSummary {
  lastB2BSyncDate: string | null;
  lastB2BSyncCount: number;
  lastChipChipSyncDate: string | null;
  lastChipChipSyncCount: number;
  unmatchedCount: number;
  todayMovements: number;
}

export interface DailyVolume {
  date: string;
  b2b: number;
  chipchip: number;
  outbound: number;
}

export interface UnmatchedProduct {
  source: 'B2B' | 'ChipChip';
  productName: string;
  lastSeen: string;
  occurrences: number;
}

export function useSyncSummary() {
  return useQuery({
    queryKey: ['sync-summary'],
    queryFn: async (): Promise<SyncSummary> => {
      const today = format(new Date(), 'yyyy-MM-dd');

      const [b2bLatest, chipchipLatest, todayTx] = await Promise.all([
        supabase
          .from('b2b_delivery_reports')
          .select('sync_date, delivery_date')
          .order('sync_date', { ascending: false })
          .limit(1)
          .single(),
        supabase
          .from('chipchip_accepted_orders')
          .select('sync_date, delivery_date')
          .order('sync_date', { ascending: false })
          .limit(1)
          .single(),
        supabase
          .from('inventory_transactions')
          .select('id', { count: 'exact', head: true })
          .gte('created_at', today),
      ]);

      // Get counts for latest sync dates
      let lastB2BSyncCount = 0;
      let lastB2BSyncDate = b2bLatest.data?.sync_date || null;
      if (lastB2BSyncDate) {
        const { count } = await supabase
          .from('b2b_delivery_reports')
          .select('id', { count: 'exact', head: true })
          .eq('sync_date', lastB2BSyncDate);
        lastB2BSyncCount = count || 0;
      }

      let lastChipChipSyncCount = 0;
      let lastChipChipSyncDate = chipchipLatest.data?.sync_date || null;
      if (lastChipChipSyncDate) {
        const { count } = await supabase
          .from('chipchip_accepted_orders')
          .select('id', { count: 'exact', head: true })
          .eq('sync_date', lastChipChipSyncDate);
        lastChipChipSyncCount = count || 0;
      }

      return {
        lastB2BSyncDate,
        lastB2BSyncCount,
        lastChipChipSyncDate,
        lastChipChipSyncCount,
        unmatchedCount: 0, // computed in unmatched query
        todayMovements: todayTx.count || 0,
      };
    },
  });
}

export function useDailyVolumes(days: number = 14) {
  return useQuery({
    queryKey: ['sync-daily-volumes', days],
    queryFn: async (): Promise<DailyVolume[]> => {
      const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');

      const [b2bData, chipchipData, txData] = await Promise.all([
        supabase
          .from('b2b_delivery_reports')
          .select('delivery_date')
          .gte('delivery_date', startDate)
          .order('delivery_date'),
        supabase
          .from('chipchip_accepted_orders')
          .select('delivery_date')
          .gte('delivery_date', startDate)
          .order('delivery_date'),
        supabase
          .from('inventory_transactions')
          .select('created_at, reference_type')
          .gte('created_at', startDate)
          .in('reference_type', ['b2b_delivery', 'chipchip_order'])
          .order('created_at'),
      ]);

      // Build date map
      const dateMap = new Map<string, DailyVolume>();
      for (let i = 0; i <= days; i++) {
        const d = format(subDays(new Date(), days - i), 'yyyy-MM-dd');
        dateMap.set(d, { date: d, b2b: 0, chipchip: 0, outbound: 0 });
      }

      (b2bData.data || []).forEach((r) => {
        const entry = dateMap.get(r.delivery_date);
        if (entry) entry.b2b++;
      });

      (chipchipData.data || []).forEach((r) => {
        const entry = dateMap.get(r.delivery_date);
        if (entry) entry.chipchip++;
      });

      (txData.data || []).forEach((r) => {
        const dateKey = r.created_at.slice(0, 10);
        const entry = dateMap.get(dateKey);
        if (entry) entry.outbound++;
      });

      return Array.from(dateMap.values());
    },
  });
}

export function useB2BSyncDetails(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['b2b-sync-details', startDate, endDate],
    queryFn: async () => {
      const [reports, transactions] = await Promise.all([
        supabase
          .from('b2b_delivery_reports')
          .select('*')
          .gte('delivery_date', startDate)
          .lte('delivery_date', endDate)
          .order('delivery_date', { ascending: false }),
        supabase
          .from('inventory_transactions')
          .select('reference_number')
          .eq('reference_type', 'b2b_delivery')
          .gte('created_at', startDate)
          .lte('created_at', endDate + 'T23:59:59'),
      ]);

      const matchedRefs = new Set(
        (transactions.data || []).map((t) => t.reference_number)
      );

      return (reports.data || []).map((r) => ({
        ...r,
        inventoryMatched: matchedRefs.has(`B2B-${r.delivery_date}-${r.product_name}`),
      }));
    },
  });
}

export function useChipChipSyncDetails(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['chipchip-sync-details', startDate, endDate],
    queryFn: async () => {
      const [orders, transactions] = await Promise.all([
        supabase
          .from('chipchip_accepted_orders')
          .select('*')
          .gte('delivery_date', startDate)
          .lte('delivery_date', endDate)
          .order('delivery_date', { ascending: false }),
        supabase
          .from('inventory_transactions')
          .select('reference_number')
          .eq('reference_type', 'chipchip_order')
          .gte('created_at', startDate)
          .lte('created_at', endDate + 'T23:59:59'),
      ]);

      const matchedRefs = new Set(
        (transactions.data || []).map((t) => t.reference_number)
      );

      return (orders.data || []).map((o) => ({
        ...o,
        inventoryMatched: matchedRefs.has(`ACCEPTED-${o.delivery_date}-${o.product_name}`),
      }));
    },
  });
}

export function useUnmatchedProducts() {
  return useQuery({
    queryKey: ['unmatched-products'],
    queryFn: async (): Promise<UnmatchedProduct[]> => {
      const thirtyDaysAgo = format(subDays(new Date(), 30), 'yyyy-MM-dd');

      const [b2bProducts, chipchipProducts, products, variations] = await Promise.all([
        supabase
          .from('b2b_delivery_reports')
          .select('product_name, delivery_date')
          .gte('delivery_date', thirtyDaysAgo),
        supabase
          .from('chipchip_accepted_orders')
          .select('product_name, delivery_date')
          .gte('delivery_date', thirtyDaysAgo),
        supabase.from('products').select('name'),
        supabase.from('product_variations').select('variation_name'),
      ]);

      const knownNames = new Set([
        ...(products.data || []).map((p) => p.name.trim().toLowerCase()),
        ...(variations.data || []).map((v) => v.variation_name.trim().toLowerCase()),
      ]);

      const unmatchedMap = new Map<string, UnmatchedProduct>();

      const processItems = (items: any[], source: 'B2B' | 'ChipChip') => {
        for (const item of items) {
          if (!knownNames.has(item.product_name.trim().toLowerCase())) {
            const key = `${source}-${item.product_name}`;
            const existing = unmatchedMap.get(key);
            if (existing) {
              existing.occurrences++;
              if (item.delivery_date > existing.lastSeen) {
                existing.lastSeen = item.delivery_date;
              }
            } else {
              unmatchedMap.set(key, {
                source,
                productName: item.product_name,
                lastSeen: item.delivery_date,
                occurrences: 1,
              });
            }
          }
        }
      };

      processItems(b2bProducts.data || [], 'B2B');
      processItems(chipchipProducts.data || [], 'ChipChip');

      return Array.from(unmatchedMap.values()).sort(
        (a, b) => b.occurrences - a.occurrences
      );
    },
  });
}
